import React, { useEffect, useState } from "react";
import { UserMinus } from "lucide-react";
import { AppButton, AppModal, Callout } from "../../ui";
import SkippedTable from "../../Students/AddToBatch/SkippedTable";
import apiClient from "../../../utils/apiClient";
import { removeStudentsOutcome } from "./batchDetails";

/**
 * RemoveStudentsModal — asks before taking students out of this batch
 * (`POST /batches/:id/remove-students`), and says what that does: they leave
 * the batch and stay enrolled in its course, fees and payments untouched, so
 * Add to batch can place them again.
 *
 * The server re-reads every student, so someone another admin moved in the
 * meantime is reported rather than pulled out. When it keeps anyone the dialog
 * stays open and says who and why; a clean removal closes it and the page
 * says so.
 *
 * Props:
 *   show, onHide
 *   batch     — { _id, name, courseName }
 *   students  — [{ _id, name }] to remove, fixed while the dialog is open
 *   terms     — useAudienceTerm()
 *   onRemoved({ removed, removedIds, skipped }, message) — after anyone left;
 *             `message` only when the dialog closes itself
 */
const RemoveStudentsModal = ({ show, onHide, batch, students = [], terms, onRemoved }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [outcome, setOutcome] = useState(null);

  useEffect(() => {
    if (!show) return;
    setBusy(false);
    setError("");
    setOutcome(null);
  }, [show]);

  const count = students.length;
  const single = count === 1 ? students[0] : null;
  const noun = (n) => (n === 1 ? terms.lower : terms.lowerPlural);
  const batchName = batch.name ? `“${batch.name}”` : "this batch";
  const subject = single ? single.name || `This ${terms.lower}` : "They";
  const course = batch.courseName || "the course";

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await apiClient.post(`/batches/${batch._id}/remove-students`, {
        studentIds: students.map((s) => s._id),
      });
      const result = removeStudentsOutcome(data, students);
      if (result.skipped.length) {
        setOutcome({ ...result, message: data.message });
        if (result.removed > 0) onRemoved(result);
      } else {
        onRemoved(result, data.message || `${count} ${noun(count)} removed from ${batchName}.`);
        onHide();
      }
    } catch (err) {
      const status = err?.response?.status;
      setError(err?.response?.data?.message || `Could not remove the ${noun(count)}. Check the roster, then try again.`);
      // A refusal (4xx) wrote nothing. A failure part-way may have removed some
      // already, so the roster is read again; the selection is left as it was.
      if (!status || status >= 500) onRemoved({ removed: 0, removedIds: [], skipped: [] });
    }
    setBusy(false);
  };

  const footer = outcome ? (
    <AppButton size="sm" onClick={onHide}>Done</AppButton>
  ) : (
    <>
      <AppButton size="sm" variant="outline" onClick={onHide} disabled={busy}>Cancel</AppButton>
      <AppButton size="sm" variant="destructive" onClick={submit} disabled={busy || !count}>
        {busy ? "Removing…" : `Remove ${single ? terms.lower : `${count} ${terms.lowerPlural}`}`}
      </AppButton>
    </>
  );

  // Short enough for one line on a phone; the batch is named in the body.
  const title = outcome
    ? "Not everyone was removed"
    : `Remove ${single ? single.name || `this ${terms.lower}` : `${count} ${terms.lowerPlural}`}?`;

  return (
    <AppModal
      show={show}
      onHide={busy ? undefined : onHide}
      title={title}
      icon={<UserMinus size={18} />}
      footer={footer}
    >
      {outcome ? (
        <div className="tw-flex tw-flex-col tw-gap-3">
          {outcome.removed > 0 && <Callout tone="success" role="status">{outcome.message}</Callout>}
          <Callout tone="warning" role="alert">
            {outcome.skipped.length} {noun(outcome.skipped.length)} {outcome.skipped.length === 1 ? "wasn’t" : "weren’t"} removed.
          </Callout>
          <SkippedTable rows={outcome.skipped} terms={terms} />
        </div>
      ) : (
        <div className="tw-flex tw-flex-col tw-gap-4">
          {error && <Callout tone="danger" role="alert">{error}</Callout>}

          {count > 1 && (
            <ul
              aria-label={`${terms.plural} to remove`}
              className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-rounded-md tw-max-h-48 tw-overflow-y-auto tw-border tw-border-solid tw-border-line tw-p-0"
            >
              {students.map((s) => (
                <li
                  key={s._id}
                  className="tw-border-0 tw-border-b tw-border-solid tw-border-line tw-px-3 tw-py-2 tw-text-sm tw-text-content last:tw-border-b-0"
                >
                  {s.name || `A ${terms.lower} on another page`}
                </li>
              ))}
            </ul>
          )}

          <ul className="tw-m-0 tw-flex tw-flex-col tw-gap-2 tw-pl-5 tw-text-sm tw-leading-normal tw-text-content">
            <li>
              {subject} will leave {batchName} and no longer join its live classes or see what is shared with it.
            </li>
            <li>
              {subject} {single ? "stays" : "stay"} enrolled in <span className="tw-font-semibold">{course}</span> without
              a batch. Fees, payments and past attendance are not changed.
            </li>
          </ul>

          <p className="tw-m-0 tw-text-[0.8125rem] tw-leading-normal tw-text-content-muted">
            To place them in another batch later, select them on the {terms.plural} page and choose
            Add to batch.
          </p>
        </div>
      )}
    </AppModal>
  );
};

export default RemoveStudentsModal;
